import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { masterDataService } from '../../services/toyService';

function CustomDataDialog({ 
  open, 
  onClose, 
  onConfirm, 
  dataType, 
  initialValue = '',
  title = null 
}) {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!value.trim()) {
      setError(t('customData.valueRequired') || 'Value is required');
      return;
    }

    if (!currentUser?.uid) {
      setError(t('customData.userNotAuthenticated') || 'User not authenticated');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Add the custom data
      await masterDataService.addCustomMasterData(currentUser.uid, dataType, value.trim());
      
      // Call the onConfirm callback with the new value
      onConfirm(value.trim());
      onClose();
    } catch (error) {
      console.error('Error adding custom data:', error);
      setError(t('customData.addError') || 'Failed to add custom data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setValue(initialValue);
    setError('');
    onClose();
  };

  const getDataTypeLabel = () => {
    const labels = {
      brands: t('common.brand') || 'Brand',
      series: t('common.series') || 'Series',
      types: t('common.type') || 'Type',
      scales: t('common.scale') || 'Scale',
      sources: t('common.source') || 'Source'
    };
    return labels[dataType] || dataType;
  };

  const getDialogTitle = () => {
    if (title) return title;
    return t('customData.addNew', { type: getDataTypeLabel() }) || `Add New ${getDataTypeLabel()}`;
  };

  const getPlaceholder = () => {
    return t('customData.enterValue', { type: getDataTypeLabel() }) || `Enter ${getDataTypeLabel().toLowerCase()} name`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {getDialogTitle()}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('customData.description', { type: getDataTypeLabel() }) || 
             `This ${getDataTypeLabel().toLowerCase()} will be added to your personal list and will only be visible to you.`}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            autoFocus
            fullWidth
            label={getDataTypeLabel()}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={getPlaceholder()}
            disabled={loading}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel') || 'Cancel'}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !value.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? (t('customData.adding') || 'Adding...') : (t('customData.add') || 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CustomDataDialog;
